import SectionHeading from "@/components/base/SectionHeading";
import Reveal from "@/components/base/Reveal";

const themes = [
  { icon: "ri-home-heart-line", title: "亲情与担当", text: "一个家如何靠彼此的付出维系，是谁在默默把日子撑起来。" },
  { icon: "ri-hand-heart-line", title: "人情与分寸", text: "邻里之间的往来、帮忙与闲话，藏着最真实的生活温度。" },
  { icon: "ri-plant-line", title: "温柔与坚韧", text: "主角用温和的方式面对压力，柔中带刚，是故事的核心气质。" },
  { icon: "ri-chat-3-line", title: "误会与和解", text: "从说不出口到终于说开，人物关系在摩擦中慢慢靠近。" },
  { icon: "ri-calendar-check-line", title: "代际理解", text: "老一辈的含蓄与年轻人的直白，在同一屋檐下相互磨合。" },
];

export default function Synopsis() {
  return (
    <section id="synopsis" className="scroll-mt-20 border-y border-background-200/70 bg-background-100">
      <div className="mx-auto w-full px-4 py-16 md:px-8 md:py-20">
        <Reveal>
          <SectionHeading
            eyebrow="作品简介"
            title="一个关于守望与温柔的家庭故事"
            description="《善良的嫂子》以家庭日常生活为背景，围绕一位性情温和、为人厚道的嫂子展开，讲述她与家人、邻里之间发生的点点滴滴。"
          />
        </Reveal>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
          <Reveal className="space-y-4">
            <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
              故事从一次归乡说起。离家多年的年轻人回到熟悉的院子，而迎接他的，是嫂子一如既往的忙碌与周到。
              一顿热饭、一盏亮着的灯、几句说不出口的关心，构成了整部作品最日常也最动人的底色。
            </p>
            <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
              随着一段旧事被重新翻出，一家人平静的生活起了波澜。误会、争执、沉默与和解交替出现，
              每个人都在学着理解身边的人。作品没有刻意戏剧化的桥段，而是把情感放在细节里，
              用最朴素的相处，讲出普通人之间的扶持与守望。
            </p>
            <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
              目前该题材已经衍生出
              <strong className="mx-1 font-semibold text-foreground-900">小说、短剧、漫画、视频</strong>
              等多种呈现形式。为了让大家看得省心，本页把各版本的目录与介绍集中整理在一起，
              你可以按自己的习惯挑选入口，也可以几种形式对照着看。
            </p>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-quill-pen-line text-primary-600" />
                适合什么样的观众
              </h3>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {[
                  "喜欢家庭情感、生活流故事的观众",
                  "想找一部能静下来慢慢看的长线作品",
                  "偏爱温柔叙事、不喜欢强冲突的读者",
                  "通勤碎片时间想刷刷短剧的上班族",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-600">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-600" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="overflow-hidden rounded-lg border border-background-200">
              <div className="h-[220px] w-full sm:h-[300px] lg:h-[340px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20painterly%20illustration%20of%20a%20cozy%20Chinese%20family%20dining%20table%20with%20home%20cooked%20dishes%20and%20a%20small%20lamp%2C%20amber%20and%20clay%20tones%2C%20soft%20nostalgic%20light%2C%20gentle%20brushstrokes%2C%20no%20text&width=1000&height=750&seq=synopsis-home-01&orientation=landscape"
                  alt="温暖色调的家常餐桌：几道家常菜与一盏小灯，烘托家庭故事的氛围"
                  className="h-full w-full object-top"
                />
              </div>
              <div className="grid grid-cols-2 divide-x divide-background-200 border-t border-background-200 bg-background-50">
                <div className="p-4">
                  <p className="font-heading text-lg font-bold text-primary-700">12 章</p>
                  <p className="mt-1 text-xs text-foreground-500">小说目录已整理</p>
                </div>
                <div className="p-4">
                  <p className="font-heading text-lg font-bold text-accent-700">12 集</p>
                  <p className="mt-1 text-xs text-foreground-500">短剧目录已齐全</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-14">
          <h3 className="font-heading text-lg font-semibold text-foreground-950">故事里的几条情感线</h3>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {themes.map((theme, index) => (
              <Reveal key={theme.title} delay={index * 70}>
                <div className="h-full rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${theme.icon} text-lg`} />
                  </span>
                  <h4 className="mt-4 font-heading text-base font-semibold text-foreground-950">{theme.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{theme.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}